import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Amazon Seller Central Management Services | Mad Marketing Club',
  description: 'End-to-end Amazon Seller Central management in India — PPC campaigns, SEO, listing optimization, FBA support & account health monitoring. 5X ROAS achieved. Get a free audit.',
  canonical: 'https://madmarketingclub.com/services/amazon',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Amazon Seller Central', url: 'https://madmarketingclub.com/services/amazon' },
  ],
  service: { name: 'Amazon Seller Central Management Services', type: 'Amazon Marketplace Management' },
};

const OVERVIEW_POINTS = [
  'Increase product visibility',
  'Improve keyword rankings',
  'Boost conversion rates',
  'Scale profitable ad campaigns',
  'Manage inventory efficiently',
  'Maintain account health',
  'Build long-term marketplace growth',
];

const APPROACH = [
  'Marketplace growth strategy',
  'Product listing optimization',
  'Amazon SEO implementation',
  'PPC campaign management',
  'Storefront and A+ content optimization',
  'Inventory and FBA support',
  'Data-driven reporting',
  'Continuous account monitoring',
];

const PROCESS = [
  {
    num: '01',
    title: 'Brand Discovery & Consultation',
    intro: 'We begin by understanding:',
    points: [
      'Your brand',
      'Product categories',
      'Business goals',
      'Existing marketplace performance',
      'Competitor landscape',
      'Revenue targets',
    ],
  },
  {
    num: '02',
    title: 'Marketplace Audit',
    intro: 'Our team performs a detailed audit of:',
    points: [
      'Product listings',
      'SEO structure',
      'PPC campaigns',
      'Account health',
      'Inventory management',
      'Conversion rates',
      'Competitor positioning',
    ],
  },
  {
    num: '03',
    title: 'Strategy Development',
    intro: 'Based on the audit, we create a marketplace growth strategy that includes:',
    points: [
      'SEO roadmap',
      'Advertising structure',
      'Product launch planning',
      'Inventory strategy',
      'Scaling opportunities',
      'Revenue growth planning',
    ],
  },
  {
    num: '04',
    title: 'Execution & Optimization',
    intro: 'We implement and continuously optimize:',
    points: [
      'Product listings',
      'Keywords',
      'Advertising campaigns',
      'A+ content',
      'Product images',
      'Backend search terms',
      'Inventory workflows',
      'Pricing strategies',
    ],
  },
  {
    num: '05',
    title: 'Performance Tracking & Reporting',
    intro: 'Regular reporting helps track:',
    points: [
      'Sales growth',
      'ROAS & ACOS',
      'Keyword rankings',
      'Conversion rates',
      'Ad performance',
      'Inventory movement',
      'Account health metrics',
    ],
  },
];

const SERVICES = [
  {
    title: 'Amazon Account Management',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller Central setup',
      'Marketplace onboarding',
      'Account health monitoring',
      'Category approvals',
      'Brand registry support',
      'Policy compliance management',
      'Suspension support',
    ],
  },
  {
    title: 'Amazon SEO Services',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Competitor keyword analysis',
      'SEO title optimization',
      'Bullet point optimization',
      'Backend search term optimization',
      'Product discoverability enhancement',
      'Organic ranking improvements',
    ],
  },
  {
    title: 'Product Listing Optimization',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'SEO-focused listings',
      'Conversion-focused product descriptions',
      'Variation setup',
      'Flat file uploads',
      'Catalog management',
      'Listing error resolution',
      'Listing suppression fixes',
    ],
  },
  {
    title: 'Amazon PPC Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Sponsored Products campaigns',
      'Sponsored Brands campaigns',
      'Sponsored Display campaigns',
      'Bid optimization',
      'Negative keyword optimization',
      'Budget scaling',
      'ACOS optimization',
      'ROAS-focused campaign management',
    ],
  },
  {
    title: 'Creative & Brand Content',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'A+ Content design',
      'Brand storefront setup',
      'Infographic design',
      'Lifestyle creatives',
      'Product image optimization',
      'Video content support',
      'Brand story creation',
    ],
  },
  {
    title: 'FBA & Inventory Management',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'FBA shipment planning',
      'Inventory forecasting',
      'Replenishment management',
      'Warehouse coordination',
      'Shipment reconciliation',
      'Stock monitoring',
      'Inventory aging analysis',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Advertising reports',
      'Profitability analysis',
      'Inventory reporting',
      'Competitor tracking',
      'Growth dashboards',
      'Marketplace performance insights',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'SEO + PPC integrated strategy',
  'Conversion-focused optimization',
  'Performance tracking & reporting',
  'Scalable growth systems',
  'Marketplace-specific expertise',
  'Long-term brand growth approach',
];

const INDUSTRIES = [
  'Beauty & skincare',
  'Fashion & apparel',
  'Electronics',
  'Home & kitchen',
  'Health & wellness',
  'FMCG',
  'Pet products',
  'D2C brands',
  'Luxury brands',
  'Consumer goods',
];

const STAGES = [
  'Launching a new brand',
  'Expanding an existing catalog',
  'Scaling advertising',
  'Improving profitability',
  'Recovering account performance',
  'Expanding into new marketplaces',
];

const ADDITIONAL = [
  'Product launch strategy',
  'Competitor benchmarking',
  'Pricing analysis',
  'Review management',
  'Customer experience monitoring',
  'Seasonal campaign planning',
  'Marketplace expansion support',
  'International selling support',
];

const FAQS = [
  {
    q: 'Do you manage complete Amazon accounts?',
    a: 'Yes, we provide end-to-end Amazon Seller Central management services including listings, PPC, SEO, inventory support, reporting, and optimization.',
  },
  {
    q: 'Can you help improve product rankings?',
    a: 'Yes. Our Amazon SEO strategies focus on keyword optimization, conversion improvements, and ranking growth.',
  },
  {
    q: 'Do you manage Amazon advertising campaigns?',
    a: 'Yes, we manage Sponsored Products, Sponsored Brands, and Sponsored Display campaigns with continuous optimization.',
  },
  {
    q: 'Can you help new brands launch on Amazon?',
    a: 'Yes, we help with marketplace onboarding, listing creation, launch strategy, advertising setup, and growth planning.',
  },
];

export default function AmazonPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Amazon Seller Central</span>
          </div>
        </nav>

        {/* HERO — single column, no image */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Amazon Seller Central</div>
            <h1 className="sp-hero-h1">Amazon Seller Central Management Services</h1>
            <p className="sp-hero-sub">Complete Amazon Marketplace Growth Solutions for Brands & Sellers</p>
            <p className="sp-hero-desc">Grow your business on Amazon with end-to-end Amazon Seller Central management services designed to improve visibility, increase conversions, optimize advertising performance, and scale revenue profitably. We help brands, startups, manufacturers, and D2C businesses manage every aspect of their Amazon operations through strategy, optimization, advertising, analytics, and marketplace growth support.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">5X</span><span className="sp-hero-stat-lbl">ROAS Achieved</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">40%+</span><span className="sp-hero-stat-lbl">Organic Sales Lift</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">98%</span><span className="sp-hero-stat-lbl">Buy Box Win Rate</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW — with Image 1 */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">Sell & Scale on Amazon with a Performance-Driven Strategy</h2>
                <p className="sp-para">Amazon is one of the largest e-commerce marketplaces globally, offering brands access to millions of active buyers across multiple product categories. Success on Amazon requires more than just listing products — it demands SEO optimization, advertising expertise, inventory planning, catalog management, and continuous account monitoring.</p>
                <p className="sp-para">Our Amazon Seller Central management services are built to help businesses:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => (
                    <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>
                  ))}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Amazon marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">Complete Marketplace Management Under One Team</h2>
            <p className="sp-para">We work as an extension of your e-commerce team and manage the complete operational and growth side of your Amazon business.</p>
            <h3 className="sp-h3">Our Approach Includes:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
            <p className="sp-para">We focus on building sustainable growth through performance optimization, brand positioning, and conversion-focused strategies.</p>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Amazon Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE — with Image 2 */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Marketplace Management Services</div>
            <h2 className="sp-h2">Data-Driven Marketplace Growth</h2>
            <p className="sp-para">We combine marketplace expertise, performance marketing, analytics, and operational management to help brands scale efficiently on Amazon.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">What Makes Us Different</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => (
                    <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>
                  ))}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Amazon marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Work With</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Marketplace Growth Solutions for Every Business Stage</h2>
            <p className="sp-para">Whether you are:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured marketplace growth strategy tailored to your business goals.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Marketplace Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ — accordion */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Scale Your Amazon Business?</h2>
            <p className="sp-cta-desc">Build a stronger marketplace presence with professional Amazon Seller Central management services focused on visibility, conversions, advertising performance, and long-term growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
